import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { transformBrandConfigData } from "../components/Partners/DataIntegrations/helperOfBrandConfig/brandConfigHelpers";
export type ReportType = "daily_user" | "daily_brand" | "monthly_brand";
type PerTabData = Record<ReportType, any[]>;
type PerTabMeta = Record<ReportType, Record<string, any>>;

// Full type definition for entire state
interface BrandConfigurationReportState {
  selectedBrandName: string;
  selectedIntegrationStatus: any[];
  selectedStuckAt: any[];
  selectedReason: any[];
  selectedAffiliatedSystem: any[];
  selectedWebsite: any[];
  selectedLastRun: string;
  brandConfigurationReportList: any[];
  brandConfigurationPIEChartsData: any[];
  brandConfigReportLoader: boolean;
  isFetchedBrandConfigReport: boolean;
  page: number;
  rowsPerPage: number;
  brandPropertiesData: any[];
  brandBackendRequirementsData: any[];
  brandConfigOverviewLoader: boolean;
  isFetchedBrandConfigOverviewData: boolean;
  brandOverviewInfoData: any[];
  brandOverviewLastRunData: any[];
  loaderForSwitchingTabs: boolean;
  brandOverviewLastRecordsData: PerTabData;
  brandOverviewAdvisorSettingsData: PerTabData;
  brandOverviewUnifierSettingsData: PerTabData;
  brandOverviewFetcherSettingsData: PerTabData;
  fetchedTabs: Record<ReportType, boolean>;
  selectedTimeFrame: any[];
  loaderForDateRangeSelection: boolean;
  overviewPerformanceData: PerTabData;
  overviewMetaData: PerTabMeta;
  objectSectionData: any[];
  objectSectionMetaData: any;
  brandAccountOverviewInfo: any[];
  rawTableData: any[];
  //--- Run Brand specific state variables ---//
  isModalOpen: boolean;
  brandDataInModal: any;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  selectedAction: string | null;
  selectedActionId: number | null;
  sort: any;
  dataCollectionBrandsList: any;
  selectedBrandConfigs: any;
  selectedUnifierDestinationTableId: number[];
  selectedBusinessAdvisorDestinationTableId: number[];
  singleRowData: any[]; // refine later if structure is known
  resWorkflowName: string | null;
  isLoading: boolean;
  isSuccess: boolean;
  fetcherConfigData: any;
  selectedProcessActions: string[];
  selectedProcessSettingMode: any;
  selectedFlowDestinationTableId: any[];
  isProcessFailed: boolean;
  processFailureMessage: any;
}

const initialState: BrandConfigurationReportState = {
  selectedBrandName: "",
  selectedIntegrationStatus: [],
  selectedStuckAt: [],
  selectedReason: [],
  selectedAffiliatedSystem: [],
  selectedWebsite: [],
  selectedLastRun: "",
  brandConfigurationReportList: [],
  brandConfigurationPIEChartsData: [],
  brandConfigReportLoader: false,
  isFetchedBrandConfigReport: false,
  page: 0,
  rowsPerPage: 10,
  brandPropertiesData: [],
  brandBackendRequirementsData: [],

  //--- Brand Configuration Overview specific state variables ---//
  brandConfigOverviewLoader: false, // loading state for brand configuration overview page
  isFetchedBrandConfigOverviewData: false, // to track if the overview data has been fetched
  brandOverviewInfoData: [],
  brandOverviewLastRunData: [],
  // brandOverviewFetcherSettingsData: [],
  loaderForSwitchingTabs: false, // loading state for the switch component in the overview page
  //Per-tab data storage
  brandOverviewLastRecordsData: {
    daily_user: [],
    daily_brand: [],
    monthly_brand: [],
  },
  brandOverviewAdvisorSettingsData: {
    daily_user: [],
    daily_brand: [],
    monthly_brand: [],
  },
  brandOverviewUnifierSettingsData: {
    daily_user: [],
    daily_brand: [],
    monthly_brand: [],
  },
  brandOverviewFetcherSettingsData: {
    daily_user: [],
    daily_brand: [],
    monthly_brand: [],
  },

  fetchedTabs: {
    daily_user: false,
    daily_brand: false,
    monthly_brand: false,
  },
  selectedTimeFrame: [],
  loaderForDateRangeSelection: false,
  overviewPerformanceData: {
    daily_user: [],
    daily_brand: [],
    monthly_brand: [],
  },
  overviewMetaData: {
    daily_user: {},
    daily_brand: {},
    monthly_brand: {},
  },
  objectSectionData: [],
  objectSectionMetaData: {},
  brandAccountOverviewInfo: [],
  rawTableData: [],

  //--- Run Brand specific state variables ---//
  isModalOpen: false, //It stores whether modal is open or not
  brandDataInModal: [] as any, //It stores selected brand's brandId, brandName, row_output_table_data (It is a array of objects which contains config_name and table_id)
  startDate: dayjs().startOf("month").format("YYYY-MM-DD"), //It stores startdate from update modal
  endDate: dayjs().format("YYYY-MM-DD"),
  //It stores enddate from update modal
  selectedAction: null, //It stores action name from update modal (Whether it is fetcher or unifier or end to end sync)
  selectedActionId: null, //It stores action id (which is required for API call) from update modal (Whether it is fetcher or unifier or end to end sync)
  sort: {
    keytoSort: "modified",
    direction: "desc",
  },
  dataCollectionBrandsList: [], //It stores data collection brands list after manually merging
  selectedBrandConfigs: [], // It stores selected brand's (when we go for fetcher) brandId, brandName, selectedConfigs (It is a array of objects which contains config_name and table_id), selectedBrandConfigs is used to store the selected brand's config data when we go for fetcher
  selectedUnifierDestinationTableId: [],
  selectedBusinessAdvisorDestinationTableId: [],
  singleRowData: [],
  resWorkflowName: null,
  isLoading: false,
  isSuccess: false,
  fetcherConfigData: [],
  selectedProcessActions: [],
  selectedProcessSettingMode: "By Flow",
  selectedFlowDestinationTableId: [],
  isProcessFailed: false,
  processFailureMessage: "",
};

const brandConfigurationReportSlice = createSlice({
  name: "brandConfigurationReport",
  initialState,
  reducers: {
    setSelectedBrandName: (state, action) => {
      state.selectedBrandName = action.payload;
    },
    setSelectedIntegrationStatus: (state, action) => {
      state.selectedIntegrationStatus = action.payload;
    },
    setSelectedStuckAt: (state, action) => {
      state.selectedStuckAt = action.payload;
    },
    setSelectedReason: (state, action) => {
      state.selectedReason = action.payload;
    },
    setSelectedAffiliatedSystem: (state, action) => {
      state.selectedAffiliatedSystem = action.payload;
    },
    setSelectedWebsite: (state, action) => {
      state.selectedWebsite = action.payload;
    },
    setSelectedLastRun: (state, action) => {
      state.selectedLastRun = action.payload;
    },
    setBrandConfigReportLoader: (state, action) => {
      state.brandConfigReportLoader = action.payload;
    },
    setBrandConfigurationReportList: (state, action) => {
      state.brandConfigurationReportList = transformBrandConfigData(
        action.payload,
      ) as any; // transformBrandConfigData is a helper functionaction.payload;
    },
    setBrandConfigurationPIEChartsData: (state, action) => {
      state.brandConfigurationPIEChartsData = action.payload;
    },
    setIsFetchedBrandConfigReport: (state, action) => {
      state.isFetchedBrandConfigReport = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setBrandPropertiesData: (state, action) => {
      state.brandPropertiesData = action.payload;
    },
    setBrandBackendRequirementsData: (state, action) => {
      state.brandBackendRequirementsData = action.payload;
    },
    setBrandConfigOverviewLoader: (state, action) => {
      state.brandConfigOverviewLoader = action.payload;
    },
    setIsFetchedBrandConfigOverviewData: (state, action) => {
      state.isFetchedBrandConfigOverviewData = action.payload;
    },
    setBrandOverviewInfoData: (state, action) => {
      state.brandOverviewInfoData = action.payload;
    },
    setBrandOverviewLastRunData: (state, action) => {
      state.brandOverviewLastRunData = action.payload;
    },

    setBrandOverviewFetcherSettingsData: (
      state,
      action: PayloadAction<{ reportType: ReportType; data: any[] }>,
    ) => {
      const { reportType, data } = action.payload;
      state.brandOverviewFetcherSettingsData[reportType] = data; //
    },
    setLoaderForSwitchingTabs: (state, action) => {
      state.loaderForSwitchingTabs = action.payload;
    },

    setBrandOverviewLastRecordsData: (
      state,
      action: PayloadAction<{ reportType: ReportType; data: any[] }>,
    ) => {
      const { reportType, data } = action.payload;
      state.brandOverviewLastRecordsData[reportType] = data;
    },
    setBrandOverviewAdvisorSettingsData: (
      state,
      action: PayloadAction<{ reportType: ReportType; data: any[] }>,
    ) => {
      const { reportType, data } = action.payload;
      state.brandOverviewAdvisorSettingsData[reportType] = data;
    },
    setBrandOverviewUnifierSettingsData: (
      state,
      action: PayloadAction<{ reportType: ReportType; data: any[] }>,
    ) => {
      const { reportType, data } = action.payload;
      state.brandOverviewUnifierSettingsData[reportType] = data;
    },

    setFetchedTabs: (state, action: PayloadAction<ReportType>) => {
      state.fetchedTabs[action.payload] = true;
    },

    setSelectedTimeFrame: (state, action) => {
      state.selectedTimeFrame = action.payload;
    },

    setLoaderForDateRangeSelection: (state, action) => {
      state.loaderForDateRangeSelection = action.payload;
    },

    setOverviewPerformanceData: (
      state,
      action: PayloadAction<{ reportType: ReportType; data: any[] }>,
    ) => {
      const { reportType, data } = action.payload;
      state.overviewPerformanceData[reportType] = data;
    },
    setOverviewMetaData: (
      state,
      action: PayloadAction<{
        reportType: ReportType;
        data: Record<string, any>;
      }>,
    ) => {
      const { reportType, data } = action.payload;
      state.overviewMetaData[reportType] = data;
    },

    setObjectSectionData: (state, action) => {
      state.objectSectionData = action.payload;
    },
    setObjectSectionMetaData: (state, action) => {
      state.objectSectionMetaData = action.payload;
    },

    setBrandAccountOverviewInfo: (state, action) => {
      state.brandAccountOverviewInfo = action.payload;
    },
    // for raw table Current changes (https://optival.atlassian.net/browse/GR-20843)
    setRawTableData: (state, action) => {
      state.rawTableData = action.payload;
    },

    resetBrandConfigurationReportState: () => initialState,
    setModalOpen: (state, action) => {
      state.isModalOpen = action.payload.isOpen;
      const data = action.payload.data;
      state.brandDataInModal = Array.isArray(data) ? data : data ? [data] : [];
      if (action.payload.isOpen) {
        state.isSuccess = false;
        state.isProcessFailed = false;
        state.processFailureMessage = "";
        state.startDate = dayjs().startOf("month").format("YYYY-MM-DD");
        state.endDate = dayjs().format("YYYY-MM-DD");
      }
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
    setSelectedAction(state, action) {
      state.selectedAction = action.payload;
    },
    setSelectedActionId(state, action) {
      state.selectedActionId = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setDataCollectionBrandsList: (state, action) => {
      state.dataCollectionBrandsList = action.payload;
    },
    setSelectedBrandConfigs: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedBrandConfigs = action.payload;
        return;
      }
      //We are making the Data Structure Here for fetcher configs
      const { brandId, brandName, configName, tableId, checked } =
        action.payload;

      if (!state.selectedBrandConfigs) {
        state.selectedBrandConfigs = [];
      }

      const brandIndex = state.selectedBrandConfigs.findIndex(
        (b: any) => b.brandId === brandId,
      );

      if (brandIndex === -1 && checked) {
        // @ts-ignore
        state.selectedBrandConfigs.push({
          brandId,
          brandName,
          selectedConfigs: [{ configName, tableId }],
        });
      } else if (brandIndex !== -1) {
        const brand: any = state.selectedBrandConfigs[brandIndex];
        const configIndex = brand.selectedConfigs.findIndex(
          (c: any) => c.tableId === tableId,
        );

        if (checked && configIndex === -1) {
          brand.selectedConfigs.push({ configName, tableId });
        } else if (!checked && configIndex !== -1) {
          brand.selectedConfigs.splice(configIndex, 1);
          if (brand.selectedConfigs.length === 0) {
            state.selectedBrandConfigs.splice(brandIndex, 1);
          }
        }
      }
    },
    setSelectedUnifierDestinationTableId: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedUnifierDestinationTableId = action.payload;
        return;
      }
      const { destinationTableId, configName, checked } = action.payload;

      if (checked) {
        const exists = state.selectedUnifierDestinationTableId.some(
          (c: any) => c.destinationTableId === destinationTableId,
        );
        if (!exists) {
          //@ts-ignore
          state.selectedUnifierDestinationTableId.push({
            destinationTableId,
            configName,
          });
        }
      } else {
        state.selectedUnifierDestinationTableId =
          state.selectedUnifierDestinationTableId.filter(
            (c: any) => c.destinationTableId !== destinationTableId,
          );
      }
    },
    setSelectedBusinessAdvisorDestinationTableId: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedBusinessAdvisorDestinationTableId = action.payload;
        return;
      }
      const { destinationTableId, configName, checked } = action.payload;

      if (checked) {
        const exists = state.selectedBusinessAdvisorDestinationTableId.some(
          (c: any) => c.destinationTableId === destinationTableId,
        );
        if (!exists) {
          //@ts-ignore
          state.selectedBusinessAdvisorDestinationTableId.push({
            destinationTableId,
            configName,
          });
        }
      } else {
        state.selectedBusinessAdvisorDestinationTableId =
          state.selectedBusinessAdvisorDestinationTableId.filter(
            (c: any) => c.destinationTableId !== destinationTableId,
          );
      }
    },
    setSingleRowData: (state, action) => {
      state.singleRowData = action.payload;
    },
    setResWorkflowName: (state, action) => {
      state.resWorkflowName = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsSuccess: (state, action) => {
      state.isSuccess = action.payload;
      if (action.payload) {
        state.isProcessFailed = false;
        state.processFailureMessage = "";
      }
    },
    setProcessFailure: (state, action) => {
      state.isProcessFailed = action.payload.isFailed;
      state.processFailureMessage = action.payload.message || "";
      if (action.payload.isFailed) {
        state.isSuccess = false;
      }
    },
    setFetcherConfigData: (state, action) => {
      state.fetcherConfigData = action.payload;
    },
    setSelectedProcessActions(state, action) {
      state.selectedProcessActions = action.payload;
    },
    setSelectedProcessSettingMode(state, action) {
      state.selectedProcessSettingMode = action.payload;
    },
    setSelectedFlowDestinationTableId: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedFlowDestinationTableId = action.payload;
        return;
      }
      const { brandId, brandName, configName, destinationTableIds, checked } =
        action.payload;
      if (checked) {
        //@ts-ignore
        state.selectedFlowDestinationTableId =
          state.selectedFlowDestinationTableId.filter(
            (c: any) => !(c.brandId === brandId && c.configName === configName),
          );
        //@ts-ignore
        state.selectedFlowDestinationTableId.push({
          brandId,
          brandName,
          configName,
          destinationTableIds,
        });
      } else {
        state.selectedFlowDestinationTableId =
          state.selectedFlowDestinationTableId.filter(
            (c: any) => !(c.brandId === brandId && c.configName === configName),
          );
      }
    },
  },
});

export const {
  setSelectedBrandName,
  setSelectedIntegrationStatus,
  setSelectedStuckAt,
  setSelectedReason,
  setSelectedAffiliatedSystem,
  setSelectedWebsite,
  setSelectedLastRun,
  setBrandConfigReportLoader,
  setBrandConfigurationReportList,
  setBrandConfigurationPIEChartsData,
  setIsFetchedBrandConfigReport,
  setPage,
  setRowsPerPage,
  setBrandPropertiesData,
  setBrandBackendRequirementsData,
  setBrandConfigOverviewLoader,
  setIsFetchedBrandConfigOverviewData,
  setBrandOverviewInfoData,
  setBrandOverviewLastRunData,
  setBrandOverviewLastRecordsData,
  setBrandOverviewAdvisorSettingsData,
  setBrandOverviewUnifierSettingsData,
  setBrandOverviewFetcherSettingsData,
  setLoaderForSwitchingTabs,
  setFetchedTabs,
  setSelectedTimeFrame,
  setLoaderForDateRangeSelection,
  setOverviewPerformanceData,
  setOverviewMetaData,
  setObjectSectionData,
  setObjectSectionMetaData,
  setBrandAccountOverviewInfo,
  setRawTableData,
  resetBrandConfigurationReportState,
  // Run Brand specific actions
  setSort,
  setDataCollectionBrandsList,
  setSingleRowData,
  setIsLoading,
  setIsSuccess,
  setModalOpen,
  setStartDate,
  setEndDate,
  setSelectedAction,
  setSelectedActionId,
  setSelectedBrandConfigs,
  setSelectedUnifierDestinationTableId,
  setSelectedBusinessAdvisorDestinationTableId,
  setResWorkflowName,
  setFetcherConfigData,
  setSelectedProcessActions,
  setSelectedProcessSettingMode,
  setSelectedFlowDestinationTableId,
  setProcessFailure,
} = brandConfigurationReportSlice.actions;
export default brandConfigurationReportSlice.reducer;
